import { createContext, useContext, useEffect, useId, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {createPortal} from 'react-dom';

export const EDITOR_TOOL_SECTIONS_KEY='tableread.editor.tool-sections.v2';
const SECTION_TITLES=['Delivery','Vocal expression','Volume','Speed','Pitch','AI direction','Vocal cues & pauses','Language'];
interface ToolDock {target:HTMLElement|null;active:string|null;titles:readonly string[];groups?:Readonly<Record<string,string>>;}
const ToolSectionsContext=createContext<{expanded:ReadonlySet<string>;toggle:(title:string)=>void;dock?:ToolDock}|null>(null);
function restoredSections(storageKey:string|undefined,defaultOpen:string) {
  try {
    const raw=storageKey?localStorage.getItem(storageKey):null;
    if(raw!==null){const saved:unknown=JSON.parse(raw);if(Array.isArray(saved)&&saved.every(v=>typeof v==='string'))return new Set(saved.filter(v=>SECTION_TITLES.includes(v)));}
  }catch{/* Expansion remains usable when storage is unavailable. */}
  return new Set([defaultOpen]);
}
/** Independent expansion state follows React portals and optionally survives sessions. */
export function EditorToolSectionGroup({children,defaultOpen='Delivery',storageKey,dock}:{children:ReactNode;defaultOpen?:string;storageKey?:string;dock?:ToolDock}) {
  const inherited=useContext(ToolSectionsContext);
  const [expanded,setExpanded]=useState(()=>restoredSections(storageKey,defaultOpen));
  useEffect(()=>{
    if(inherited||!storageKey)return;
    try{localStorage.setItem(storageKey,JSON.stringify([...expanded].filter(v=>SECTION_TITLES.includes(v))));}catch{/* Best-effort preference; never affects the screenplay. */}
  },[expanded,inherited,storageKey]);
  const toggle=(title:string)=>setExpanded(current=>{const next=new Set(current);if(next.has(title))next.delete(title);else next.add(title);return next;});
  return inherited?<>{children}</>:<ToolSectionsContext.Provider value={{expanded,toggle,dock}}>{children}</ToolSectionsContext.Provider>;
}

/** Click-only tool divisions. Keep children mounted so hiding a section never resets its draft controls. */
export default function EditorToolSection({ title, children, defaultOpen = false, fill = false, scroll = true, grouped = false, displayTitle=title, headerAction, contentClassName='' }: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  fill?: boolean;
  scroll?: boolean;
  grouped?: boolean;
  displayTitle?:string;
  headerAction?:ReactNode;
  contentClassName?:string;
}) {
  const id = useId();
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const group=useContext(ToolSectionsContext);
  const dockTitle=group?.dock?.groups?.[title]??title;
  if(group?.dock?.titles.includes(dockTitle)) {
    // Keep draft inputs mounted while switching icons; no screenplay mutation.
    return group.dock.target?createPortal(<section hidden={group.dock.active!==dockTitle} aria-label={`${title} tools`}>
      <div className="flex items-center justify-between gap-2"><h3 id={`${id}-heading`} className={dockTitle===title&&!headerAction?'sr-only':'text-xs font-semibold'}>{displayTitle}</h3>{headerAction}</div>
      <div id={`${id}-content`} role="region" aria-labelledby={`${id}-heading`} className={`space-y-3 ${contentClassName}`}>{children}</div>
    </section>,group.dock.target):null;
  }
  const open=grouped&&group?group.expanded.has(title):localOpen;
  return <section className={`rounded-lg border bg-muted/20 ${fill&&open?'flex min-h-0 flex-1 flex-col':''}`} aria-label={`${title} tools`}>
    <h3 className="sticky top-0 z-10 flex items-center rounded-t-lg bg-card">
      <button type="button" id={`${id}-heading`} aria-expanded={open} aria-controls={`${id}-content`}
        className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold focus-visible:outline-2 focus-visible:outline-ring"
        onClick={() => grouped&&group?group.toggle(title):setLocalOpen(current=>!current)}>
        {displayTitle}<ChevronDown aria-hidden="true" className={`h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
      </button>
      {headerAction&&<span className="shrink-0 pr-2">{headerAction}</span>}
    </h3>
    <div id={`${id}-content`} role="region" aria-labelledby={`${id}-heading`} hidden={!open}
      className={`${scroll?'editor-themed-scroll max-h-[32rem] overflow-y-auto overscroll-contain':''} ${fill?'min-h-0 flex-1 lg:max-h-none':''} space-y-3 border-t p-3 ${contentClassName}`}>
      {children}
    </div>
  </section>;
}
